<template>
  <div class="mt-8 px-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-semibold text-gray-700">User Management</h1>
        <p class="text-sm text-gray-500 mt-1">Total Users: {{ Users.length }}</p>
      </div>
      <button
        @click="showAddForm = !showAddForm"
        class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
      >
        <fa-icon icon="plus" class="mr-2"></fa-icon>
        {{ showAddForm ? "Cancel" : "Add New User" }}
      </button>
    </div>

    <!-- Add User Form -->
    <div v-if="showAddForm" class="mb-6 p-6 bg-white rounded-lg shadow-sm border-l-4 border-primary">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">Add New User</h2>
      <form @submit.prevent="register" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              v-model="formData.username"
              type="text"
              placeholder="Enter username"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              v-model="formData.email"
              type="email"
              placeholder="Enter email"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              v-model="formData.password"
              type="password"
              placeholder="Enter password"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              v-model="formData.role"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
              required
            >
              <option value="">Select Role</option>
              <option value="ADMIN">Admin</option>
              <option value="EDITOR">Editor</option>
            </select>
          </div>
        </div>

        <!-- Error Alert -->
        <div v-if="errorsData.isVisible" class="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <p v-for="(msg, i) in errorsData.messages" :key="i" class="text-sm">
            {{ msg }}
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <button
            type="button"
            @click="showAddForm = false"
            class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSending"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
          >
            <fa-icon v-if="isSending" icon="spinner" :spin="true" class="mr-2" />
            {{ isSending ? "Creating..." : "Create User" }}
          </button>
        </div>
      </form>
    </div>

    <!-- Success Message -->
    <transition
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="isSuccess" class="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
        <fa-icon icon="check-circle" class="mr-2"></fa-icon>
        User created successfully!
      </div>
    </transition>

    <!-- Users List -->
    <div class="bg-white rounded-lg shadow-sm p-6">
      <div class="flex flex-col md:flex-row gap-4 mb-6">
        <!-- Filter by Role -->
        <div class="flex-1 relative">
          <fa-icon icon="filter" class="absolute left-3 top-3 text-gray-400"></fa-icon>
          <select
            v-model="roleSelect"
            @change="searchedByRole"
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
          >
            <option value="all">All Roles</option>
            <option value="Super admin">Super Admin</option>
            <option value="Admin">Admin</option>
            <option value="Editor">Editor</option>
          </select>
        </div>

        <!-- Search -->
        <div class="flex-1 relative">
          <fa-icon icon="search" class="absolute left-3 top-3 text-gray-400"></fa-icon>
          <input
            v-model="research"
            @input="searchedByName"
            type="text"
            placeholder="Search username or email..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <!-- Users Table -->
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-sm text-left text-gray-600">
          <thead class="bg-gray-50 border-b text-gray-700 font-semibold">
            <tr>
              <th class="px-6 py-3">User Info</th>
              <th class="px-6 py-3">Role</th>
              <th class="px-6 py-3">Created</th>
              <th class="px-6 py-3">Updated</th>
              <th class="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y">
            <tr v-for="u in arrayUsers[page]" :key="u.id" class="hover:bg-gray-50 transition-colors">
              <!-- User Info -->
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <img :src="u.imgUrl || 'https://via.placeholder.com/40'" alt="avatar" class="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p class="font-medium text-gray-900">{{ u.username }}</p>
                    <p class="text-xs text-gray-500">{{ u.email }}</p>
                  </div>
                </div>
              </td>

              <!-- Role -->
              <td class="px-6 py-4">
                <span
                  :class="[
                    'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
                    u.role === 'Super admin'
                      ? 'bg-red-100 text-red-800'
                      : u.role === 'Admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  ]"
                >
                  {{ u.role }}
                </span>
              </td>

              <!-- Created -->
              <td class="px-6 py-4 text-sm text-gray-600">{{ u.createAt }}</td>

              <!-- Updated -->
              <td class="px-6 py-4 text-sm text-gray-600">{{ u.updateAt }}</td>

              <!-- Actions -->
              <td class="px-6 py-4 text-right space-x-2">
                <button
                  v-if="role === 'SUPER_ADMIN' && u.role !== 'Super admin'"
                  @click="openEditUser(u)"
                  class="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-900 font-medium rounded hover:bg-blue-50 transition-colors"
                  title="Edit user role"
                >
                  <fa-icon icon="edit" class="mr-1"></fa-icon>Edit
                </button>
                <button
                  @click="openMoreUser(u)"
                  class="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 font-medium rounded hover:bg-gray-100 transition-colors"
                  title="View user details"
                >
                  <fa-icon icon="eye" class="mr-1"></fa-icon>View
                </button>
              </td>
            </tr>
            <tr v-if="!arrayUsers[page] || arrayUsers[page].length === 0">
              <td colspan="5" class="px-6 py-8 text-center text-gray-500">
                <fa-icon icon="inbox" class="mr-2"></fa-icon>
                No users found
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="arrayUsers.length > 0" class="flex justify-center gap-2 mt-6">
        <button
          v-for="i in arrayUsers.length"
          :key="i"
          @click="changePage(i)"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-colors',
            page === i - 1
              ? 'bg-primary text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          ]"
        >
          {{ i }}
        </button>
      </div>
    </div>

    <!-- Edit User Modal -->
    <transition
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="editOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md">
          <div class="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-800">Edit User Role</h3>
            <button @click="editOpen = false" class="text-gray-500 hover:text-gray-700">
              <fa-icon icon="times" class="text-xl"></fa-icon>
            </button>
          </div>

          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                :value="user.username"
                type="text"
                disabled
                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                :value="user.email"
                type="email"
                disabled
                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                v-model="user._role"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
              >
                <option value="ADMIN">Admin</option>
                <option value="EDITOR">Editor</option>
              </select>
            </div>
          </div>

          <div class="flex justify-end gap-2 p-6 border-t border-gray-200">
            <button
              @click="editOpen = false"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="updateRole"
              :disabled="isSendingModal"
              class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
            >
              <fa-icon v-if="isSendingModal" icon="spinner" :spin="true" class="mr-2" />
              {{ isSendingModal ? "Updating..." : "Update Role" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- View User Modal -->
    <transition
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="moreOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md">
          <div class="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-800">User Details</h3>
            <button @click="moreOpen = false" class="text-gray-500 hover:text-gray-700">
              <fa-icon icon="times" class="text-xl"></fa-icon>
            </button>
          </div>

          <div class="p-6 text-center">
            <img
              :src="user.imgUrl || 'https://via.placeholder.com/80'"
              alt="avatar"
              class="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-gray-200"
            />
            <h3 class="text-xl font-bold text-gray-800">{{ user.username }}</h3>
            <p class="text-sm text-gray-500">{{ user.email }}</p>

            <div class="space-y-3 mt-6 text-left bg-gray-50 rounded-lg p-4">
              <div class="flex justify-between items-center">
                <span class="text-gray-600 font-medium">Role:</span>
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-xs font-medium',
                    user.role === 'Super admin'
                      ? 'bg-red-100 text-red-800'
                      : user.role === 'Admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  ]"
                >
                  {{ user.role }}
                </span>
              </div>
              <div class="flex justify-between border-t border-gray-200 pt-3">
                <span class="text-gray-600 font-medium">Created:</span>
                <span class="text-gray-800">{{ user.createAt }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Updated:</span>
                <span class="text-gray-800">{{ user.updateAt }}</span>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-2 p-6 border-t border-gray-200">
            <button
              @click="moreOpen = false"
              class="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { dateFormat } from "@/assets/Methods";

export default {
  name: "Users",
  data() {
    return {
      formData: {
        username: "",
        email: "",
        password: "",
        role: ""
      },
      user: {
        email: "",
        username: "",
        role: "",
        _role: "",
        imgUrl: "",
        id: "",
        createAt: "",
        updateAt: ""
      },
      research: "",
      roleSelect: "all",
      page: 0,
      Users: [],
      arrayUsers: [],
      editOpen: false,
      moreOpen: false,
      showAddForm: false,
      isSuccess: false,
      isSending: false,
      isSendingModal: false,
      errorsData: {
        isVisible: false,
        messages: []
      }
    };
  },
  computed: {
    ...mapState({
      role: (state) => state.auth?.user?.role || "EDITOR"
    })
  },
  created() {
    this.getUsers();
  },
  methods: {
    // Get all users
    async getUsers() {
      try {
        const { data } = await this.$store.dispatch("getUsers");
        this.Users = data;
        this.showFive();
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    },

    // Register new user
    async register() {
      if (!this.formData.username || !this.formData.email || !this.formData.password || !this.formData.role) {
        this.errorsData = {
          isVisible: true,
          messages: ["All fields are required"]
        };
        return;
      }

      this.isSending = true;
      try {
        await this.$axios.post("/auth/register", {
          username: this.formData.username,
          email: this.formData.email,
          password: this.formData.password,
          role: this.formData.role
        });

        this.isSuccess = true;
        this.formData = {
          username: "",
          email: "",
          password: "",
          role: ""
        };
        this.errorsData = { isVisible: false, messages: [] };
        this.showAddForm = false;

        setTimeout(() => {
          this.isSuccess = false;
          this.getUsers();
        }, 2000);
      } catch (err) {
        this.errorsData = {
          isVisible: true,
          messages: err.response?.data?.message
            ? [err.response.data.message]
            : ["Error creating user"]
        };
      } finally {
        this.isSending = false;
      }
    },

    // Filter by role
    searchedByRole() {
      this.page = 0;
      const filteredUsers =
        this.roleSelect === "all"
          ? this.Users
          : this.Users.filter((u) => u.role === this.roleSelect);
      this.arrayUsers = this.showFiveHelper(filteredUsers);
    },

    // Search by name
    searchedByName() {
      this.page = 0;
      const filtered = this.Users.filter(
        (u) =>
          u.username.toLowerCase().includes(this.research.toLowerCase()) ||
          u.email.toLowerCase().includes(this.research.toLowerCase())
      );
      this.arrayUsers = this.showFiveHelper(filtered);
    },

    // Helper function to split data into chunks
    showFiveHelper(data) {
      const newData = [];
      let counter = 0;
      let index = 0;

      while (counter < data.length) {
        newData[index] = [];

        for (let i = 0; i < 5 && counter < data.length; i++) {
          const user = data[counter];
          newData[index].push({
            ...user,
            createAt: dateFormat(user.createAt || user.createdAt),
            updateAt: dateFormat(user.updateAt || user.updatedAt)
          });
          counter++;
        }
        index++;
      }

      return newData;
    },

    // Initial pagination
    showFive() {
      this.arrayUsers = this.showFiveHelper(this.Users);
    },

    // Change page
    changePage(p) {
      this.page = p - 1;
    },

    // Open edit modal
    openEditUser(u) {
      this.user = {
        ...u,
        _role: this.roleReverseFormat(u.role)
      };
      this.editOpen = true;
    },

    // Open more info modal
    openMoreUser(u) {
      this.user = {
        ...u
      };
      this.moreOpen = true;
    },

    // Update user role
    async updateRole() {
      this.isSendingModal = true;
      try {
        await this.$axios.patch(`/users/${this.user.id}`, {
          role: this.user._role
        });

        this.editOpen = false;
        this.getUsers();
        this.searchedByRole();
      } catch (err) {
        console.error("Error updating user role:", err);
      } finally {
        this.isSendingModal = false;
      }
    },

    // Helper: Format role for display
    roleFormat(role) {
      const roles = {
        ADMIN: "Admin",
        EDITOR: "Editor",
        SUPER_ADMIN: "Super admin"
      };
      return roles[role] || role;
    },

    // Helper: Format role for API
    roleReverseFormat(role) {
      const roles = {
        Admin: "ADMIN",
        Editor: "EDITOR",
        "Super admin": "SUPER_ADMIN"
      };
      return roles[role] || role;
    }
  }
};
</script>

<style scoped>
.primary {
  @apply bg-blue-600;
}
</style>
